import { apim } from "@/constants/api"
import { createAsyncThunk, createSlice } from "@reduxjs/toolkit"
import { HYDRATE } from "next-redux-wrapper"
import { store } from "@/store"
import { CATALOG_PROJECTION_ENDPOINT } from "@/constants/routes"
import {
  favoritesEndPoint,
  addFavoritesEndPoint,
  createFavoritesEndPoint,
  removeFavoritesEndPoint,
  FAVORITES_LIST_END_POINT,
  FAVORITES_LIST_LIMIT,
  USER_LIST_PRODUCT_DELETE_POINT,
  USER_LIST_DELETE_END_POINT,
} from "@/constants/index"
import { checkIsFavorite, getUserPersona, getProductFlgs } from "@/utils/helper"
import { addDataLayerFav } from "@/utils/favouritesHelper"
import { getConfig } from "@/constants/config"
import { SEARCH_FULL_CATALOG } from "@/constants/routes"
import {
  getUserListLineItems,
  getMaxVersionLists,
  getMovieToListAndDeleteFormListItems,
  getMultiDeleteItemsPayLoad,
} from "@/utils/ApiUtil"
const initialState = {
  favorites: {},
  status: "",
  fullFavoriteData: {},
  isLoginFavouriteCallback: { isCallBack: false, callBackAssetID: "" },
  saveToListLineItems: {},
  userListItemsSkus: {},
  userLists: [],
}
const shoppingListName = "Favorites"

export const getFavorites = createAsyncThunk("getFavorites", async () => {
  const config = await getConfig()
  const { internationalization: { locale: newLocale = "en-US" } = {} } = config
  const locale = newLocale === "fr-CA" ? "en-CA" : newLocale
  const response = await apim.get(`${favoritesEndPoint}?lang=${locale}`)
  const { results } = response?.data ?? { results: [] }
  if (results.length > 0) {
    const getVersion = Math.max(...results.map(i => i.version))
    const favoritesData = results.filter(
      item => item.name[locale] === "Favorites" && item.version === getVersion
    )
    const { textLineItems = [] } = favoritesData[0]
    if (textLineItems.length > 0) checkIsFavorite(textLineItems)
    if (favoritesData.length) {
      return favoritesData[0]
    }
  }
  return {}
})

export const deleteUserList = createAsyncThunk("deleteUserList", async list => {
  const response = await apim.delete(
    `${USER_LIST_DELETE_END_POINT}/${list?.id}?version=${list?.version}`
  )
  return response
})

export const deleteProductFromList = createAsyncThunk(
  "deleteProductFromList",
  async list => {
    const response = await apim.post(
      `${USER_LIST_PRODUCT_DELETE_POINT}/${list?.listId}`,
      {
        version: list.listVersion,
        actions: [
          {
            action: "removeLineItem",
            lineItemId: list.id,
          },
        ],
      }
    )
    return response
  }
)

export const getMyAccountListsWithLineItems = createAsyncThunk(
  "getMyAccountListsWithLineItems",
  async () => {
    const config = await getConfig()
    const { internationalization: { locale: newLocale = "en-US" } = {} } =
      config
    const locale = newLocale === "fr-CA" ? "en-CA" : newLocale
    const response = await apim.get(
      `${FAVORITES_LIST_END_POINT}?lang=${locale}&limit=${FAVORITES_LIST_LIMIT}`
    )
    const { results } = response?.data ?? { results: [] }
    const maxVersionLists = getMaxVersionLists(results, locale)
    const listItems = getUserListLineItems(maxVersionLists)
    return [listItems, maxVersionLists]
  }
)

export const getMyAccountLists = createAsyncThunk(
  "getMyAccountLists",
  async () => {
    const config = await getConfig()
    const { internationalization: { locale: newLocale = "en-US" } = {} } =
      config
    const locale = newLocale === "fr-CA" ? "en-CA" : newLocale
    const response = await apim.get(
      `${FAVORITES_LIST_END_POINT}?lang=${locale}&limit=${FAVORITES_LIST_LIMIT}`
    )
    const { results } = response?.data ?? { results: [] }
    return getMaxVersionLists(results, locale)
  }
)

export const getListFirstLineItemsData = createAsyncThunk(
  "getListFirstLineItemsData",
  async (list, allItems = false) => {
    const { lineItems = [] } = list
    if (lineItems.length) {
      const config = await getConfig()
      const productId = lineItems[0]?.productId
      const productIds = lineItems.map(item => item.productId)
      const persona = getUserPersona()
      const fl = getProductFlgs(persona)
      const language = config?.internationalization?.language ?? "en"
      const res = await apim.get(`/search/plp`, {
        params: {
          q: "*:*",
          fq: `ctId_s:("${
            allItems ? productIds.join('","') : productId
          }") && language_s:("${language}")`,
          rows: allItems ? productIds?.length : FAVORITES_LIST_LIMIT,
          fl: fl.join(","),
          collections: "kohler",
          profilename: "profile_kohler_Full_Catalog",
        },
      })
      const productData = res.data?.response?.docs ?? []
      return productData
    }
  }
)

export const getMyAccountFavorites = createAsyncThunk(
  "getMyAccountFavorites",
  async query => {
    const config = await getConfig()
    const { internationalization: { locale: newLocale = "en-US" } = {} } =
      config
    const locale = newLocale === "fr-CA" ? "en-CA" : newLocale
    const response = await apim.get(`${favoritesEndPoint}?lang=${locale}`)
    const { results } = response.data
    if (results?.length > 0) {
      const getVersion = Math.max(...results.map(i => i.version))
      const d = results.filter(
        i => i.name[locale] === "Favorites" && i.version === getVersion
      )[0]
      if (d) {
        const { lineItems = [], textLineItems = [] } = d
        const productIds = lineItems.map(item => item.productId)
        const persona = getUserPersona()

        const fl = [
          "masterSKU_s",
          "SKUColorSwatchFilename_ss",
          "ProductDescriptionProductShort_s",
          "ProductBrandName_s",
          "metaTitle_s",
          "productName_s",
          "id",
          "productImages.url_ss",
          "slug_s",
          "Product_Category",
          "displayPriceStartingAt_s",
          "Tile_Title",
          "ProductIsExclusive_s",
          "RegionReleaseforShipment_dt",
          "Color.SKU.Details_ss",
          "productImages.labelWithUrl_ss",
          "variantList.sku_ss",
          "RegionBrandName_s",
          `priceList.${persona}.discountedPrice_d`,
          `priceList.${persona}.saleOffer_s`,
          `priceList.${persona}.finalPrice_d`,
          `priceList.${persona}.price_d`,
          `Color.${persona}.Details_ss`,
          "ctId_s",
          "Brand_ss",
          "Country_ss",
        ]
        const language = config?.internationalization?.language ?? "en"
        const canadianPrice =
          config?.apiEndPointData?.map?.canadianPrice ?? null

        const params = {
          q: "*:*",
          fq: `ctId_s:("${productIds.join(
            '","'
          )}") && language_s:("${language}")`,
          fl: fl.join(","),
          collections: config?.general?.siteName ?? "Kohler",
          start: 0,
          rows: 50,
        }
        // Conditionally add `canadianPrice` if it has a value
        if (canadianPrice) {
          params.CanadianPrice = canadianPrice
        }

        const res = await apim.get(SEARCH_FULL_CATALOG, { params })
        const productData = res.data?.response?.docs ?? []
        if (productData.length > 0) {
          lineItems.forEach((item, index, items) => {
            const product = productData.find(
              product => product["ctId_s"] === item?.productId
            )
            items[index].productData = product
          })
        }

        let sortLineItems = []
        if (lineItems.length > 0) {
          sortLineItems = lineItems.sort(function (a, b) {
            return new Date(b.addedAt) - new Date(a.addedAt)
          })
        }
        let sortTextLineItems = []
        if (textLineItems.length > 0) {
          sortTextLineItems = textLineItems.sort(function (a, b) {
            return new Date(b.addedAt) - new Date(a.addedAt)
          })
        }
        return {
          sortProducts: sortLineItems ?? [],
          sortMedia: sortTextLineItems ?? [],
          d,
        }
      } else {
        await store.dispatch(createFavorite())
      }
    }

    return null
  }
)

export const createFavorite = createAsyncThunk("createFavorite", async () => {
  const config = await getConfig()
  const { internationalization: { locale: newLocale = "en-US" } = {} } = config
  const locale = newLocale === "fr-CA" ? "en-CA" : newLocale
  const response = await apim.post(`${createFavoritesEndPoint}`, {
    name: { [locale]: shoppingListName },
  })
  return response.data ?? {}
})

export const getProductBySlug = createAsyncThunk(
  "list/getProductBySlug",
  async payload => {
    const { slug, currencyCode, sku, persona } = payload
    try {
      let responseProductObject = {}
      const obj = {}
      const { data: { results = [] } = {} } = await apim.get(
        `${CATALOG_PROJECTION_ENDPOINT}?where=slug(en-US%3D"${slug}") or masterVariant(sku in ("${sku}")) or variants(sku in ("${sku}"))`
      )
      responseProductObject = results[0] ?? {}
      if (responseProductObject?.masterVariant) {
        let product = {}
        const {
          masterVariant: { sku: masterSku = "" } = {},
          masterVariant = {},
          variants = [],
        } = responseProductObject
        if (sku === masterSku) {
          product = masterVariant
        } else {
          product = variants.find(variant => sku === variant.sku)
        }

        const fixedPromo = product?.prices?.find(
          price =>
            price.value?.currencyCode === currencyCode &&
            price.customerGroup?.obj?.key === persona &&
            price.custom?.fields?.fixedPrice
        )
        const productPrice = product?.prices?.find(
          price =>
            price.value?.currencyCode === currencyCode &&
            price.customerGroup?.obj?.key === persona
        )
        let priceObj = fixedPromo || productPrice
        if (!priceObj?.value) {
          priceObj = product?.prices?.find(
            price =>
              price.value?.currencyCode === currencyCode && !price.customerGroup
          )
        }

        if (priceObj?.discounted) {
          obj[sku] = Number(
            ((priceObj?.discounted?.value?.centAmount ?? 0) / 100).toFixed(2)
          )
          obj[`${sku}-discount`] = Number(
            ((priceObj?.value?.centAmount ?? 0) / 100).toFixed(2)
          )
        } else {
          obj[sku] = Number(
            ((priceObj?.value?.centAmount ?? 0) / 100).toFixed(2)
          )
        }
        const regionIsBackorderable = `${sku}-RegionIsBackorderable`
        const comboSku = `${sku}-ComboSKU`
        const discontinuedDate = `${sku}-DiscontinuedDate`
        const regionIsBackorderableRec = product?.attributes.find(
          rec => rec.name === "RegionIsBackorderable"
        )
        const comboSkuRec = product?.attributes.find(
          rec => rec.name === "ComboSKU"
        )
        const discontinuedSkuDate = product?.attributes.find(
          rec => rec.name === "RegionDiscontinuedDate"
        )
        if (
          regionIsBackorderableRec &&
          regionIsBackorderableRec?.name === "RegionIsBackorderable"
        ) {
          obj[regionIsBackorderable] = regionIsBackorderableRec?.value ?? false
        } else {
          obj[regionIsBackorderable] = false
        }

        if (
          discontinuedSkuDate &&
          discontinuedSkuDate?.name === "RegionDiscontinuedDate"
        ) {
          const discontinuedDateValue = discontinuedSkuDate?.value ?? ""
          if (discontinuedDateValue) {
            const discontinuedDateObj = new Date(discontinuedDateValue)
            const now = new Date()
            const discontinuedDateOnly = new Date(
              discontinuedDateObj.getFullYear(),
              discontinuedDateObj.getMonth(),
              discontinuedDateObj.getDate()
            )
            const nowDateOnly = new Date(
              now.getFullYear(),
              now.getMonth(),
              now.getDate()
            )
            obj[discontinuedDate] = discontinuedDateOnly <= nowDateOnly
          } else {
            obj[discontinuedDate] = false
          }
        } else {
          obj[discontinuedDate] = false
        }
        comboSkuRec ? (obj[comboSku] = true) : (obj[comboSku] = false)
        return obj
      } else {
        obj[sku] = 0
        obj[`${sku}-RegionIsBackorderable`] = false
        obj[`${sku}-ComboSKU`] = false
        obj[`${sku}-DiscontinuedDate`] = false
        return obj
      }
    } catch (err) {
      return err
    }
  }
)
export const addListLineItemQuantity = createAsyncThunk(
  "addListLineItemQuantity",
  async data => {
    const {
      listId,
      version,
      productId,
      lineItemId,
      quantity,
      variantId,
      action,
    } = data

    const actionObject = {
      action: "addLineItem",
      productId: productId,
      variantId: variantId,
      quantity: quantity,
      custom: {
        type: {
          key: "lineItemShoppingList",
          typeId: "type",
        },
      },
    }
    if (action === "subtract") {
      actionObject.action = "removeLineItem"
      actionObject.lineItemId = lineItemId
    }
    const body = {
      version,
      actions: [actionObject],
    }

    const response = await apim.post(
      `${addFavoritesEndPoint(listId, "product")}`,
      body
    )
    return response?.data
  }
)
export const moveItemsFromList = createAsyncThunk(
  "moveItemsFromList",
  async data => {
    const {
      list,
      listData,
      currentList,
      productData,
      selectedItems,
      productQtyObj,
    } = data
    let listId
    let version
    if (list?.id && list?.version) {
      listId = list?.id
      version = list?.version
    } else {
      const config = await getConfig()
      const { internationalization: { locale: newLocale = "en-US" } = {} } =
        config
      const locale = newLocale === "fr-CA" ? "en-CA" : newLocale
      const response = await apim.post(`${createFavoritesEndPoint}`, {
        name: { [locale]: listData?.name },
        project: { [locale]: listData?.project },
        description: { [locale]: listData?.description },
        custom: {
          type: {
            key: "shoppingList",
          },
          fields: {
            project: listData?.project ?? "",
          },
        },
      })
      listId = response?.data?.id ?? ""
      version = response?.data?.version ?? ""
    }
    const formattedData = getMovieToListAndDeleteFormListItems(
      selectedItems,
      currentList,
      productData,
      productQtyObj
    )
    const deleteResponse = await apim.post(
      `${USER_LIST_PRODUCT_DELETE_POINT}/${currentList?.id}`,
      {
        version: currentList?.version,
        actions: formattedData[0],
      }
    )
    const body = {
      version,
      actions: formattedData[1],
    }
    const addResponse = await apim.post(
      `${addFavoritesEndPoint(listId, "product")}`,
      body
    )
    return {
      deleteResponse: deleteResponse?.data,
      addResponse: addResponse?.data,
    }
  }
)

export const deleteMultiProductFromList = createAsyncThunk(
  "deleteMultiProductFromList",
  async data => {
    const { list, selectedItems } = data
    const payload = getMultiDeleteItemsPayLoad(list, selectedItems)
    const deleteResponse = await apim.post(
      `${USER_LIST_PRODUCT_DELETE_POINT}/${list?.id}`,
      {
        version: list?.version,
        actions: payload,
      }
    )
    return deleteResponse
  }
)

export const addProductToList = createAsyncThunk(
  "addProductToList",
  async data => {
    const { sku, list, listData, createList } = data
    let listId
    let version
    if (list?.id && list?.version) {
      listId = list?.id
      version = list?.version
    } else {
      const config = await getConfig()
      const { internationalization: { locale: newLocale = "en-US" } = {} } =
        config
      const locale = newLocale === "fr-CA" ? "en-CA" : newLocale
      const response = await apim.post(`${createFavoritesEndPoint}`, {
        name: { [locale]: listData?.name },
        project: { [locale]: listData?.project },
        description: { [locale]: listData?.description },
        custom: {
          type: {
            key: "shoppingList",
          },
          fields: {
            project: listData?.project ?? "",
          },
        },
      })
      listId = response?.data?.id ?? ""
      version = response?.data?.version ?? ""
      if (createList) {
        return response?.data
      }
    }
    const body = {
      version,
      actions: [
        {
          action: "addLineItem",
          sku,
          custom: {
            type: {
              key: "lineItemShoppingList",
              typeId: "type",
            },
          },
        },
      ],
    }
    const response = await apim.post(
      `${addFavoritesEndPoint(listId, "product")}`,
      body
    )
    await store.dispatch(getMyAccountListsWithLineItems())
    return response?.data
  }
)

export const editList = createAsyncThunk("editList", async data => {
  const { list, listData = {} } = data
  const config = await getConfig()
  const { internationalization: { locale: newLocale = "en-US" } = {} } = config
  const locale = newLocale === "fr-CA" ? "en-CA" : newLocale
  let listId
  let version
  if (list?.id && list?.version) {
    listId = list?.id
    version = list?.version
  }
  const body = {
    version,
    actions: [
      {
        action: "changeName",
        name: {
          [locale]: listData?.name,
        },
      },
      {
        action: "setDescription",
        description: {
          [locale]: listData?.description,
        },
      },
      {
        action: "setCustomType",
        type: {
          key: "shoppingList",
        },
        fields: {
          project: listData?.project,
        },
      },
    ],
  }
  const response = await apim.post(
    `${addFavoritesEndPoint(listId, "product")}`,
    body
  )

  return response?.data
})

export const addProduct = createAsyncThunk(
  "addProduct",
  async (sku, thunkAPI) => {
    await store.dispatch(getFavorites())
    const state = thunkAPI.getState()
    let id
    let version
    let favorites
    const isPdp = window.eventPageType === "pdp"
    if (
      state?.favorites.favorites !== undefined &&
      Object.keys(state?.favorites.favorites).length > 0
    ) {
      favorites = state?.favorites?.favorites
      id = favorites?.id
      version = favorites?.version
    } else {
      const favoritesMain = await store.dispatch(createFavorite())
      favorites = favoritesMain?.payload

      id = favorites?.id
      version = favorites?.version
    }

    const body = {
      version,
      actions: [
        {
          action: "addLineItem",
          sku,
          custom: {
            type: {
              key: "lineItemShoppingList",
              typeId: "type",
            },
          },
        },
      ],
    }

    const response = await apim.post(
      `${addFavoritesEndPoint(id, "product")}`,
      body
    )
    if (isPdp && response.data) {
      addDataLayerFav(
        "",
        true,
        "add",
        window.internalLinkType ?? "",
        window.zoneName ?? "",
        "",
        ""
      )
    }
    return response.data
  }
)

export const removeItems = createAsyncThunk(
  "removeItems",
  async (ids, thunkAPI) => {
    let isDataLayerNeeded = true
    let imageName = ""
    let section = ""
    if (!Array.isArray(ids)) {
      isDataLayerNeeded = ids?.isDataLayerNeeded
      imageName = ids?.imageName
      section = ids?.section
      ids = ids.ids
    }
    try {
      const state = thunkAPI.getState()
      const { id, version, lineItems } = state?.favorites.favorites || {}
      if (id) {
        const actions = ids.map(pid => {
          if (lineItems.find(el => el.id === pid)) {
            return {
              action: "removeLineItem",
              lineItemId: pid,
            }
          } else {
            return {
              action: "removeTextLineItem",
              textLineItemId: pid,
            }
          }
        })

        const response = await apim.post(`${removeFavoritesEndPoint(id)}`, {
          version,
          actions,
        })
        isDataLayerNeeded &&
          addDataLayerFav(
            "",
            true,
            "remove",
            window.internalLinkType ?? "",
            section ?? "",
            "",
            "",
            imageName
          )
        return response.data
      }
      return {}
    } catch (error) {
      isDataLayerNeeded &&
        addDataLayerFav(
          "",
          false,
          "remove",
          window.internalLinkType ?? "",
          window.zoneName ?? "",
          "",
          ""
        )
    }
  }
)

export const myAccountRemoveItems = createAsyncThunk(
  "myAccountRemoveItems",
  async (ids, thunkAPI) => {
    const state = thunkAPI.getState()
    const { id, version, lineItems } =
      state?.favorites?.fullFavoriteData?.d || {}
    if (id) {
      const actions = ids.map(pid => {
        if (lineItems.find(el => el.id === pid)) {
          return {
            action: "removeLineItem",
            lineItemId: pid,
          }
        } else {
          return {
            action: "removeTextLineItem",
            textLineItemId: pid,
          }
        }
      })

      const response = await apim.post(`${removeFavoritesEndPoint(id)}`, {
        version,
        actions,
      })
      if (response?.data) {
        const { lineItems = [], textLineItems = [] } = response?.data || {}
        const productIds = lineItems.map(item => item.productId)
        const persona = getUserPersona()

        const fl = [
          "masterSKU_s",
          "SKUColorSwatchFilename_ss",
          "ProductDescriptionProductShort_s",
          "ProductBrandName_s",
          "metaTitle_s",
          "productName_s",
          "id",
          "productImages.url_ss",
          "slug_s",
          "Product_Category",
          "displayPriceStartingAt_s",
          "Tile_Title",
          "ProductIsExclusive_s",
          "RegionReleaseforShipment_dt",
          "Color.SKU.Details_ss",
          "productImages.labelWithUrl_ss",
          "variantList.sku_ss",
          "RegionBrandName_s",
          `priceList.${persona}.discountedPrice_d`,
          `priceList.${persona}.saleOffer_s`,
          `priceList.${persona}.finalPrice_d`,
          `priceList.${persona}.price_d`,
          `Color.${persona}.Details_ss`,
          "ctId_s",
          "Brand_ss",
          "Country_ss",
        ]

        const config = await getConfig()
        const language = config?.internationalization?.language ?? "en"
        const res = await apim.get(`/search/full-catalog`, {
          params: {
            q: "*:*",
            fq: `ctId_s:("${productIds.join(
              '","'
            )}") && language_s:("${language}")`,
            fl: fl.join(","),
            collections: config?.general?.siteName ?? "Kohler",
            start: 0,
            rows: 50,
          },
        })

        const productData = res.data?.response?.docs ?? []
        if (productData.length > 0) {
          lineItems.forEach((item, index, items) => {
            const product = productData.find(
              product => product["ctId_s"] === item?.productId
            )
            items[index].productData = product
          })
        }

        let sortLineItems = []
        if (lineItems.length > 0) {
          sortLineItems = lineItems.sort(function (a, b) {
            return new Date(b.addedAt) - new Date(a.addedAt)
          })
        }
        let sortTextLineItems = []
        if (textLineItems.length > 0) {
          sortTextLineItems = textLineItems.sort(function (a, b) {
            return new Date(b.addedAt) - new Date(a.addedAt)
          })
        }
        return {
          sortProducts: sortLineItems ?? [],
          sortMedia: sortTextLineItems ?? [],
          d: response?.data ?? {},
        }
      }
    }
    return {}
  }
)

export const addMedia = createAsyncThunk(
  "addMedia",
  async (params, thunkAPI) => {
    const config = await getConfig()
    const { internationalization: { locale: newLocale = "en-US" } = {} } =
      config
    const locale = newLocale === "fr-CA" ? "en-CA" : newLocale
    const {
      type = "",
      name = "",
      url = "",
      description = "",
      articleType = "",
      thumbnail = "",
      needDataLayerToAdd = true,
      caption = "",
      section = "",
      videoName = "",
      videoId = "",
      errorMessage = "",
      successMsg = "",
      successCallback = () => {},
      errorCallback = () => {},
      imageName = "",
    } = params
    try {
      const state = thunkAPI.getState()
      let id
      let version
      const fav = await store.dispatch(getFavorites())
      let favorites =
        Object.keys(state?.favorites?.favorites)?.length > 0
          ? state?.favorites?.favorites
          : fav?.payload
      if (favorites?.id && favorites?.version) {
        id = favorites?.id
        version = favorites?.version
      } else {
        const favoritesMain = await store.dispatch(createFavorite())
        favorites = favoritesMain?.payload
        id = favorites?.id
        version = favorites?.version
      }
      const body = {
        version: version,
        actions: [
          {
            action: "addTextLineItem",
            name: {
              [locale]: name || videoName || "",
            },
            description: {
              [locale]: description,
            },
            custom: {
              type: {
                key: "textLineItemShoppingList",
                typeId: "type",
              },
              fields: {
                itemType: type,
                link: url,
                thumbnail,
                articleType,
              },
            },
          },
        ],
      }
      const response = await apim.post(
        `${addFavoritesEndPoint(id, type)}`,
        body
      )
      const { textLineItems = [] } = response.data
      if (textLineItems.length > 0) {
        checkIsFavorite(textLineItems)
        successCallback()
      }
      if (needDataLayerToAdd)
        addDataLayerFav(
          successMsg,
          true,
          "add",
          window.internalLinkType ?? caption,
          section,
          videoName,
          videoId,
          imageName
        )

      return response.data
    } catch (error) {
      if (needDataLayerToAdd)
        addDataLayerFav(
          errorMessage,
          false,
          "add",
          caption,
          section,
          videoName,
          videoId
        )

      errorCallback()
    }
  }
)

export const favoritesSlice = createSlice({
  name: "favorites",
  initialState,
  reducers: {
    setFavorites(state, action) {
      state.favorites = action.payload
    },
    setLoginFavouriteCallback(state, action) {
      state.isLoginFavouriteCallback = action.payload
    },
  },
  extraReducers(builder) {
    builder
      .addCase([HYDRATE], (state, action) => {
        return {
          ...state,
          ...action.payload.favorites,
        }
      })
      .addCase(getFavorites.pending, (state, action) => {
        state.status = "loading"
      })
      .addCase(getFavorites.fulfilled, (state, action) => {
        state.favorites = action.payload

        state.status = "succeeded"
      })
      .addCase(getFavorites.rejected, (state, action) => {
        state.status = "failed"
        state.error = action.error.message
      })
      .addCase(getMyAccountListsWithLineItems.pending, (state, action) => {
        state.status = "loading"
      })
      .addCase(getMyAccountListsWithLineItems.fulfilled, (state, action) => {
        state.saveToListLineItems = action.payload[0][0]
        state.userListItemsSkus = action.payload[0][1]
        state.userLists = action.payload[1]
        state.status = "succeeded"
      })
      .addCase(getMyAccountListsWithLineItems.rejected, (state, action) => {
        state.status = "failed"
        state.error = action.error.message
      })
      .addCase(getMyAccountFavorites.pending, (state, action) => {
        state.status = "loading"
      })
      .addCase(getMyAccountFavorites.fulfilled, (state, action) => {
        state.fullFavoriteData = action.payload

        state.status = "succeeded"
      })
      .addCase(getMyAccountFavorites.rejected, (state, action) => {
        state.status = "failed"
        state.error = action.error.message
      })

      .addCase(myAccountRemoveItems.pending, (state, action) => {
        state.status = "loading"
      })
      .addCase(myAccountRemoveItems.fulfilled, (state, action) => {
        state.fullFavoriteData = action.payload

        state.status = "succeeded"
      })
      .addCase(myAccountRemoveItems.rejected, (state, action) => {
        state.status = "failed"
        state.error = action.error.message
      })
      .addCase(addProduct.pending, (state, action) => {
        state.status = "loading"
      })
      .addCase(addProduct.fulfilled, (state, action) => {
        state.favorites = action.payload

        state.status = "succeeded"
      })
      .addCase(addProduct.rejected, (state, action) => {
        state.status = "failed"
        state.error = action.error.message
      })
      .addCase(createFavorite.pending, (state, action) => {
        state.status = "loading"
      })
      .addCase(createFavorite.fulfilled, (state, action) => {
        state.favorites = action.payload
        state.status = "succeeded"
      })
      .addCase(createFavorite.rejected, (state, action) => {
        state.status = "failed"
        state.error = action.error.message
      })
      .addCase(addMedia.pending, (state, action) => {
        state.status = "loading"
      })
      .addCase(addMedia.fulfilled, (state, action) => {
        state.favorites = { ...state.favorites, ...action.payload }
        state.status = "succeeded"
      })
      .addCase(addMedia.rejected, (state, action) => {
        state.status = "failed"
        state.error = action.error.message
      })
      .addCase(removeItems.pending, (state, action) => {
        state.status = "loading"
      })
      .addCase(removeItems.fulfilled, (state, action) => {
        state.favorites = action.payload
        state.status = "succeeded"
      })
      .addCase(removeItems.rejected, (state, action) => {
        state.status = "failed"
        state.error = action.error.message
      })
  },
})

export const { setFavorites, setLoginFavouriteCallback } =
  favoritesSlice.actions
export const selectFavoritesState = state => state.favorites

export default favoritesSlice.reducer
